/**
 * Shared pagination mechanism for marketing listing pages (blog, tools, integrations).
 *
 * One knob controls how many items render per page across every listing — bump
 * `LISTING_PAGE_SIZE` and all three listings re-paginate, no per-page code changes.
 */

/** Default number of cards shown per listing page. Change here to re-tune every listing. */
export const LISTING_PAGE_SIZE = 12

export interface PaginatedResult<T> {
  /** Items belonging to the current page only. */
  items: T[]
  /** Current page, clamped to [1, totalPages]. */
  page: number
  /** Total number of pages (always >= 1). */
  totalPages: number
  /** Total number of items across all pages. */
  total: number
  /** Page size used for this result. */
  pageSize: number
}

/** Parse a raw `?page=` search param into a 1-based page number (defaults to 1). */
export function parsePageParam(raw: string | string[] | undefined): number {
  const value = Array.isArray(raw) ? raw[0] : raw
  const n = Number.parseInt(value ?? "1", 10)
  return Number.isFinite(n) && n >= 1 ? n : 1
}

/** Slice `items` down to the requested page. Page is clamped into range so an
 * out-of-bounds `?page=` never yields an empty grid. */
export function paginate<T>(
  items: T[],
  page: number,
  pageSize: number = LISTING_PAGE_SIZE,
): PaginatedResult<T> {
  const total = items.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const current = Math.min(Math.max(1, page), totalPages)
  const start = (current - 1) * pageSize
  return {
    items: items.slice(start, start + pageSize),
    page: current,
    totalPages,
    total,
    pageSize,
  }
}

/** Build a listing href, preserving the active category and page in a stable
 * order. `all` category and page 1 are omitted to keep canonical URLs clean. */
export function buildListingHref(
  basePath: string,
  params: { category?: string; page?: number; q?: string },
): string {
  const qs = new URLSearchParams()
  if (params.category && params.category !== "all") {
    qs.set("category", params.category)
  }
  // Before `page`, so a shared URL reads `?category=&q=&page=` in one order no
  // matter which control the visitor touched last.
  const query = normalizeListingQuery(params.q)
  if (query) {
    qs.set("q", query)
  }
  if (params.page && params.page > 1) {
    qs.set("page", String(params.page))
  }
  const s = qs.toString()
  return s ? `${basePath}?${s}` : basePath
}

/** Longest `?q=` this accepts. Anything past it is a paste, not a search, and
 *  it would otherwise end up in a canonical URL and an analytics property. */
const MAX_LISTING_QUERY = 80

/** Normalize a raw `?q=` into the single form used for filtering, links and
 *  analytics: trimmed, inner whitespace collapsed, length-capped. Empty string
 *  means "no search", which is what keeps it out of `buildListingHref`. */
export function normalizeListingQuery(
  raw: string | string[] | undefined,
): string {
  const value = firstSearchParam(raw)
  if (!value) return ""
  return value.trim().replace(/\s+/g, " ").slice(0, MAX_LISTING_QUERY)
}

/**
 * Filter listing items by a free-text query.
 *
 * EVERY term must match, and a term may match ANY of the fields — so "video
 * effect" finds a card whose title says "AI VFX" only if the description
 * carries both words, which is the behaviour someone typing two words expects.
 * Matching is substring, not word-boundary: half the catalogue is named in
 * compounds ("upscaler", "voiceover"), and a prefix search on those returns
 * nothing for the obvious query.
 *
 * Lives here rather than in the tools page because the category pills, the
 * pagination and the canonical URL all already come from this module, and the
 * next listing that wants a search box should not reimplement the matcher.
 */
export function filterListingByQuery<T>(
  items: T[],
  q: string,
  fields: (item: T) => (string | undefined)[],
): T[] {
  const query = normalizeListingQuery(q)
  if (!query) return items
  const terms = query.toLowerCase().split(" ")
  return items.filter(item => {
    const haystack = fields(item)
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
    return terms.every(term => haystack.includes(term))
  })
}

function firstSearchParam(raw: string | string[] | undefined): string | undefined {
  return Array.isArray(raw) ? raw[0] : raw
}

/** Normalize a raw category param to an allowed listing category or `all`. */
export function normalizeListingCategory(
  raw: string | string[] | undefined,
  allowedCategories: readonly string[],
): string {
  const category = firstSearchParam(raw)
  if (!category || category === "all") return "all"
  return allowedCategories.includes(category) ? category : "all"
}

/** Build the canonical path for listing metadata from raw search params. */
export function buildListingMetadataPath(
  basePath: string,
  params: {
    category?: string | string[]
    page?: string | string[]
    totalPages?: number
    allowedCategories?: readonly string[]
  },
): string {
  const requestedPage = parsePageParam(params.page)
  const totalPages = Math.max(1, params.totalPages ?? requestedPage)

  // `q` is deliberately NOT carried into the canonical. A search result page is
  // a slice of a listing that already has a URL, and every distinct query would
  // otherwise mint its own canonical — an unbounded set of near-duplicate pages
  // built from whatever visitors type. The page pairs this with `noindex`.
  return buildListingHref(basePath, {
    category: params.allowedCategories
      ? normalizeListingCategory(params.category, params.allowedCategories)
      : firstSearchParam(params.category),
    page: Math.min(requestedPage, totalPages),
  })
}

/** Compute the page tokens to render: first, last, a window around current, and
 * `"ellipsis"` placeholders for the gaps. */
export function paginationRange(
  page: number,
  totalPages: number,
  windowSize = 1,
): Array<number | "ellipsis"> {
  const out: Array<number | "ellipsis"> = []
  for (let p = 1; p <= totalPages; p++) {
    const inWindow = p >= page - windowSize && p <= page + windowSize
    if (p === 1 || p === totalPages || inWindow) {
      out.push(p)
    } else if (out[out.length - 1] !== "ellipsis") {
      out.push("ellipsis")
    }
  }
  return out
}
